import { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaVenusMars,
  FaCalendarAlt,
  FaStethoscope,
  FaComment,
} from "react-icons/fa";

const EnquiryForm = () => {
  const services = [
    "Clinical Biochemistry",
    "Hematology Tests",
    "Microbiology & Infectious Disease Testing",
    "Histopathology & Cytopathology",
    "Molecular & Genetic Testing",
  ];
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "",
    DOB: "",
    speciality: "",
    query: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (message) setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/inquiries/send-inquiry`, formData); // Updated endpoint to match router
      if (response.status === 201) {
        toast.success("Enquiry submitted successfully!");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          gender: "",
          DOB: "",
          speciality: "",
          query: "",
        });
        setMessage("Thank you! We'll get back to you soon.");
      }
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          toast.error(data.message || "Please fill all fields correctly.");
          setMessage(data.message || "Please check your details and try again.");
        } else if (status === 500) {
          toast.error("Server error. Please try again later.");
          setMessage("Submission failed due to a server issue.");
        } else {
          toast.error("Failed to submit enquiry. Please try again.");
          setMessage("An unexpected error occurred.");
        }
      } else {
        toast.error("Network error. Please check your connection.");
        setMessage("Unable to connect to the server.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 py-12 px-4 sm:px-6 lg:px-10">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-[#0BB6D9] to-[#38cfed] bg-clip-text text-transparent">
            Submit Your Enquiry
          </h1>
          <p className="text-gray-600 max-w-md mx-auto">
            We're here to help. Fill out the form below and we'll respond
            promptly.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
            {/* First & Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First Name"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300"
                />
              </div>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last Name"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300"
                />
              </div>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300"
                />
              </div>
            </div>

            {/* Gender & Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <FaVenusMars className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300 appearance-none"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
                <input
                  type="date"
                  name="DOB"
                  value={formData.DOB}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300"
                />
              </div>
            </div>

            {/* Speciality */}
            <div className="relative">
              <FaStethoscope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0BB6D9] text-lg" />
              <select
                name="speciality"
                value={formData.speciality}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300 appearance-none"
                required
              >
                <option value="">Select Speciality</option>
                {services.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </div>

            {/* Query */}
            <div className="relative">
              <FaComment className="absolute left-3 top-3 text-[#0BB6D9] text-lg" />
              <textarea
                name="query"
                value={formData.query}
                onChange={handleChange}
                placeholder="Describe your query in detail..."
                rows="4"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-gray-50 text-gray-700 border border-gray-200 focus:border-cyan-500 focus:outline-none focus:bg-white transition-all duration-300 resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-all duration-300 transform ${loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-[#0BB6D9] to-[#38cfed] hover:from-[#208ba0] hover:to-[#4d9bab] hover:shadow-lg hover:scale-105"
                }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Enquiry"
              )}
            </button>
          </form>

          {/* Message */}
          {message && (
            <div
              className={`px-6 pb-6 text-center text-sm transition-all duration-300 ${message.includes("success") || message.includes("Thank")
                ? "text-green-600"
                : "text-red-600"
                }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryForm;